//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'

//#endregion

import { IOpData, IOpObject } from '../../models/util/interfaces'
import { Enums } from "../../models/common/enums"
import { OpActor, OpConsumer, OpConsumerData, OpDataTypeInGroup} from "../../models/core"
import { OpActorData, OpConsumerNotificationContent } from "../../models/custom"
import { OpDataList } from '../../models//util/op-data-list'

import { CommonHelper, ConvertHelper, DataProvider, LocalizationHelper, LogHelper, ToastHelper } from '../../helpers'

import { CreateEditInitParams } from '../common/create-edit-popup/create-edit-popup.controller'

export class ConsumerHelper {

    // #region Consts

    public static get FILE_NAME_PREFIX(): string { return 'consumers' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdConsumer }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Consumer }

    public static get LOG_PREFIX(): string { return '[SIMAX.Models.Consumers]' }

    // #endregion


    // #region Methods

    public static deleteConsumer(dataProvider: DataProvider, idConsumer: number, moduleName: Enums.SimaxModuleName): JQueryPromise<OpConsumer> {
        const toDelete: OpConsumer = new OpConsumer()

        toDelete.idConsumer = idConsumer

        return dataProvider.Consumer
            .deleteConsumer(toDelete)
            .then((consumer: OpConsumer) => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, moduleName))

                return consumer
            })
            .fail((error: any) => {
                const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, moduleName))
                LogHelper.log(ConsumerHelper.LOG_PREFIX, `Unable to delete consumer. Error: ${errorMsg}.`)

                return $.Deferred<OpConsumer>().reject(error).promise()
            })
    }

    public static saveConsumer(dataProvider: DataProvider, consumer: OpConsumer, moduleName: Enums.SimaxModuleName, isBulkSave: boolean = false): JQueryPromise<OpConsumer> {
        return dataProvider.Consumer
            .saveConsumer(consumer)
            .then((savedObject: OpConsumer) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, moduleName))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.consumerSaved, true, 0)
                }
                return savedObject
            })
            .fail((error: any) => {
                if (!isBulkSave) {
                    const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                    ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, moduleName))
                    console.warn(ConsumerHelper.LOG_PREFIX, `Unable to save consumer. Error: ${errorMsg}.`)
                }
                return $.Deferred<OpConsumer>().reject(error).promise()
            })
    }

    public static createConsumerObject(object: IOpObject, consumerDataTypesToEdit: OpDataTypeInGroup[], actorDataTypesToEdit: OpDataTypeInGroup[]): OpConsumer {
        const newObjectConsumer: OpConsumer = new OpConsumer(null)
        const newObjectActor: OpActor = new OpActor(null)

        const consumerIdDataTypes: number[] = consumerDataTypesToEdit.map((element: OpDataTypeInGroup) => element.idDataType)
        const consumerData: OpConsumerData[] = object.dataList
            .toArray()
            .filter((element: IOpData) => _.contains(consumerIdDataTypes, element.idDataType))
            .map((element: IOpData) => OpConsumerData.fromIOpData(element))

        const actorIdDataTypes: number[] = actorDataTypesToEdit.map((element: OpDataTypeInGroup) => element.idDataType)
        const actorData: OpActorData[] = object.dataList
            .toArray()
            .filter((element: IOpData) => _.contains(actorIdDataTypes, element.idDataType))
            .map((element: IOpData) => OpActorData.fromIOpData(element))

        newObjectConsumer.idConsumer = object.idObject
        newObjectConsumer.opState = object.opState
        newObjectConsumer.dataList.push(...consumerData)

        newObjectActor.dataList.push(...actorData)

        newObjectConsumer.actor = newObjectActor

        return newObjectConsumer
    }

    public static getCreateEditOptions(referenceObjectId: number = 0, referenceObject: OpConsumer = null): CreateEditInitParams {
        return {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: ConsumerHelper.REFERENCE_TYPE,
            moduleName: ConsumerHelper.SIMAX_MODULE_NAME,
            referenceObject: referenceObject,
            referenceObjectId: referenceObjectId,
            sections: null,
            useBusinessObjects: true
        }
    }

    public static getFakeObjectWithFullDataList(referenceValue: OpConsumer): OpConsumer {
        const consumerDataList: OpDataList<OpConsumerData> = referenceValue.dataList != null ? referenceValue.dataList : new OpDataList<OpConsumerData>()
        const actorDataList: OpDataList<OpActorData> = referenceValue.actor != null ? referenceValue.actor.dataList : new OpDataList<OpActorData>()

        const consumerMadeFromActorDataList: OpDataList<OpConsumerData> = new OpDataList<OpConsumerData>()

        actorDataList.toArray().forEach((data: OpActorData) => {
            const newData: OpConsumerData = new OpConsumerData()

            newData.idConsumerData = data.idActorData,
            newData.idConsumer = data.idActor,
            newData.idDataType = data.idDataType,
            newData.indexNbr = data.indexNbr,
            newData.value = data.value,

            consumerMadeFromActorDataList.push(newData)
        })

        let fakeObject: OpConsumer = new OpConsumer()
        fakeObject.idConsumer = referenceValue.idConsumer
        fakeObject.opState = referenceValue.opState

        let fakeDataList: OpDataList<OpConsumerData> = new OpDataList<OpConsumerData>()

        fakeDataList.push(...consumerDataList.toArray(), ...consumerMadeFromActorDataList.toArray())
        fakeObject.dataList = fakeDataList

        return fakeObject
    }

    public static getNotificationsGridColumns(): DevExpress.ui.dxDataGridColumn[] {
        const idNotificationDeliveryType: DevExpress.ui.dxDataGridColumn = {
            dataField: 'idNotificationDeliveryType',
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotificationDeliveryType),
            allowEditing: false,
            visibleIndex: 1,
            calculateDisplayValue: (rowData: OpConsumerNotificationContent) => {
                return Enums.NotificationDeliveryType[rowData.idNotificationDeliveryType]
            }
        }

        const destinationAddress: DevExpress.ui.dxDataGridColumn = {
            dataField: 'destinationAddress',
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Address),
            allowEditing: false,
            visibleIndex: 2
        }

        const activity: DevExpress.ui.dxDataGridColumn = {
            dataField: 'isActive',
            dataType: 'boolean',
            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Active),
            allowEditing: true,
            visibleIndex: 0
        }

        return [activity, destinationAddress, idNotificationDeliveryType]
    }

    // #endregion
}